import { DEMO_SPOTLIGHT_DATA } from "@/data/demoSpotlightData";
import { SpotlightHero } from "@/components/spotlight/SpotlightHero";
import { SpotlightSpeedToSold } from "@/components/spotlight/SpotlightSpeedToSold";
import { SpotlightDigitalFootprint } from "@/components/spotlight/SpotlightDigitalFootprint";
import { SpotlightMarketComparison } from "@/components/spotlight/SpotlightMarketComparison";
import { SpotlightHomesCarousel } from "@/components/spotlight/SpotlightHomesCarousel";
import { SpotlightMap } from "@/components/spotlight/SpotlightMap";
import { SpotlightShowings } from "@/components/spotlight/SpotlightShowings";
import { SpotlightReviews } from "@/components/spotlight/SpotlightReviews";
import { SpotlightTeam } from "@/components/spotlight/SpotlightTeam";
import { SpotlightCTA } from "@/components/spotlight/SpotlightCTA";
import { SpotlightSocialLinks } from "@/components/spotlight/SpotlightSocialLinks";
import northLogo from "@/assets/north-logo.png";

const d = DEMO_SPOTLIGHT_DATA;
const { settings, team, agents, homes, stats, market, showings, reviews, sold_listings, view_sources } = d;

// Use North logo as the team logo for the demo
const companyLogoUrl = northLogo;

// Resolve colors: custom overrides > team defaults
const primaryColor = settings.custom_primary_color || team.default_primary_color;
const secondaryColor = settings.custom_secondary_color || team.default_secondary_color;

// Build AI headline (mirrors Spotlight.tsx logic)
function buildDemoHeadline(): string {
  const teamDom = settings.custom_avg_dom ?? stats.avg_dom;
  const marketDom = market.market_median_dom;
  if (teamDom > 0 && marketDom > 0 && teamDom < marketDom) {
    const pct = Math.round(((marketDom - teamDom) / marketDom) * 100);
    if (pct >= 20) return `Selling ${pct}% Faster Than the Market`;
  }
  const career = settings.zillow_sales_count_all_time;
  if (career && career >= 1000) return `${career.toLocaleString()}+ Homes Sold and Counting`;
  if (career && career >= 100) return `${career.toLocaleString()} Homes Sold and We're Just Getting Started`;
  return "Your Trusted Real Estate Partner";
}

function buildDemoTagline(): string | null {
  if (settings.subheadline) return null;
  const fragments: string[] = [];
  const career = settings.zillow_sales_count_all_time;
  const teamDom = settings.custom_avg_dom ?? stats.avg_dom;
  const marketDom = market.market_median_dom;
  const headlineUsedSpeed = teamDom > 0 && marketDom > 0 && teamDom < marketDom
    && Math.round(((marketDom - teamDom) / marketDom) * 100) >= 20;

  if (career && career >= 1000) fragments.push(`${career.toLocaleString()}+ homes sold and counting`);
  else if (career && career >= 100) fragments.push(`${career.toLocaleString()} homes sold`);

  if (!headlineUsedSpeed && teamDom > 0 && marketDom > 0 && teamDom < marketDom) {
    const pct = Math.round(((marketDom - teamDom) / marketDom) * 100);
    if (pct >= 20) fragments.push(`selling ${pct}% faster than the market`);
  }

  const lastYear = settings.zillow_sales_count_last_year;
  if (lastYear && lastYear >= 50) fragments.push(`${lastYear} transactions in the last year`);

  if (fragments.length === 0) return null;
  return fragments.map((f, i) => i === 0 ? f.charAt(0).toUpperCase() + f.slice(1) : f).join(", ") + ".";
}

const headline = settings.headline || buildDemoHeadline();
const tagline = buildDemoTagline() || settings.subheadline;

// Hero image: use the selected listing's image
const selectedNorth = settings.hero_listing_north_id
  ? homes.find((h) => h.id === settings.hero_listing_north_id)
  : null;
const listingImageUrl = selectedNorth?.image_url ?? (homes.length > 0 ? homes[0].image_url : null);
const heroImageUrl = settings.hero_media_type === "photo" && settings.hero_custom_url
  ? settings.hero_custom_url
  : listingImageUrl;
const heroVideoUrl = settings.hero_media_type === "video" && settings.hero_custom_url
  ? settings.hero_custom_url
  : null;

// Featured image for CTA
const featuredImageUrl = [...homes]
  .sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
  .find((h) => h.listing_status && /active|for.sale/i.test(h.listing_status))
  ?.image_url ?? homes[0]?.image_url ?? null;

const hasSocialLinks = settings.social_facebook || settings.social_instagram ||
  settings.social_linkedin || settings.social_twitter ||
  settings.social_youtube || settings.social_tiktok;

export default function DemoSpotlight() {
  return (
    <div className="min-h-screen">
      <SpotlightHero
        teamName={team.name}
        companyLogoUrl={companyLogoUrl}
        headline={headline}
        subheadline={tagline}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        heroImageUrl={heroImageUrl}
        heroVideoUrl={heroVideoUrl}
      />

      {settings.show_speed_to_sold && settings.zillow_sales_count_all_time && (
        <SpotlightSpeedToSold
          salesCountAllTime={settings.zillow_sales_count_all_time}
          salesCountLastYear={settings.zillow_sales_count_last_year}
          avgValueThreeYear={settings.zillow_avg_value_three_year}
          priceRangeMin={settings.zillow_price_range_min}
          priceRangeMax={settings.zillow_price_range_max}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_views_saves && (
        <SpotlightDigitalFootprint
          portalViews={stats.total_portal_views}
          portalSaves={stats.total_portal_saves}
          totalViews={stats.total_views}
          totalSaves={stats.total_saves}
          homes={homes as any}
          primaryColor={primaryColor}
          viewSources={view_sources as any}
        />
      )}

      {settings.show_market_comparison && market && (
        <SpotlightMarketComparison
          market={market}
          teamAvgDom={settings.custom_avg_dom ?? stats.avg_dom}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_homes_carousel && homes.length > 0 && (
        <SpotlightHomesCarousel
          homes={homes as any}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_map && sold_listings && sold_listings.length > 0 && (
        <SpotlightMap
          soldListings={sold_listings as any}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_showings && showings && showings.length > 0 && (
        <SpotlightShowings
          showings={showings as any}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_reviews && reviews && reviews.length > 0 && (
        <SpotlightReviews
          reviews={reviews as any}
          reviewSummary={settings.review_summary}
          totalReviewCount={settings.zillow_total_reviews}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_team_section && agents.length > 0 && (
        <SpotlightTeam
          agents={agents}
          teamName={team.name}
          primaryColor={primaryColor}
        />
      )}

      <SpotlightCTA
        ctaText={settings.cta_text}
        ctaLink={settings.cta_link}
        contactEmail={settings.contact_email}
        contactPhone={settings.contact_phone}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        featuredImageUrl={featuredImageUrl}
      />

      <footer className="py-8 px-6 text-center bg-[#f8fafc] border-t border-slate-100">
        {hasSocialLinks && <SpotlightSocialLinks links={settings} primaryColor={primaryColor} />}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          <span className="text-xs text-slate-400">Powered by</span>
          <img src={northLogo} alt="North Reports" className="h-4" />
        </div>
      </footer>
    </div>
  );
}
